/** A state worn as a pill or a glyph: a run's status, a case's outcome. */

import { Check, CircleSlash, Contrast, TriangleAlert, X } from "lucide-react";
import { cn } from "~/lib/utils";
import { RUN_STATUS_CONFIG } from "~/pages/manifold-nextgen/components/evals/constants/runs";
import {
  CASE_OUTCOME_CONFIG,
  CaseOutcome,
} from "~/pages/manifold-nextgen/components/evals/constants/scoring";
import { EvalRunStatus } from "~/pages/manifold-nextgen/types/eval-run";

/** A tinted pill, for the two things on this surface that carry a state. */
const Badge = ({ tone, children }: { tone: string; children: React.ReactNode }) => (
  <span
    className={cn(
      "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
      tone
    )}
  >
    {children}
  </span>
);

/** A run's lifecycle status, with a pulse while it is still in flight. */
export const RunStatusBadge = ({ status }: { status: EvalRunStatus }) => {
  const config = RUN_STATUS_CONFIG[status] ?? RUN_STATUS_CONFIG[EvalRunStatus.PENDING];
  const isLive = status === EvalRunStatus.PENDING || status === EvalRunStatus.RUNNING;

  return (
    <Badge tone={config.badgeClass}>
      {isLive && <span className="size-1.5 animate-pulse rounded-full bg-current" aria-hidden />}
      {config.label}
    </Badge>
  );
};

/**
 * The glyph for a non-passing matrix cell. Shapes are the accessibility channel
 * for the pass/fail hue pair, so each survives at 12px.
 */
export const OutcomeGlyph = ({
  outcome,
  className,
}: {
  outcome: CaseOutcome;
  className?: string;
}) => {
  const glyph = CASE_OUTCOME_CONFIG[outcome].glyph;
  if (glyph === "failed") {
    return <X className={cn("size-3", className)} aria-hidden />;
  }
  if (glyph === "mixed") {
    return <Contrast className={cn("size-3", className)} aria-hidden />;
  }
  if (glyph === "errored") {
    return <TriangleAlert className={cn("size-3", className)} aria-hidden />;
  }
  return null;
};

/** The outcome as a labelled chip, for tables and drawers rather than the grid. */
export const OutcomeBadge = ({ outcome }: { outcome: CaseOutcome }) => {
  const config = CASE_OUTCOME_CONFIG[outcome];

  return (
    <Badge tone={config.badgeClass}>
      {outcome === CaseOutcome.NOT_RUN ? (
        <CircleSlash className="size-3" aria-hidden />
      ) : outcome === CaseOutcome.PASSED ? (
        <Check className="size-3" aria-hidden />
      ) : (
        <OutcomeGlyph outcome={outcome} />
      )}
      {config.label}
    </Badge>
  );
};
